import { useTranslation } from 'react-i18next';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

const PHOTOS = [
  {
    src: "https://static.readdy.ai/image/13e0fae89bbbd02d36954a6d83bda5fb/baf102470391d79aef159c577eec1b4f.png",
    captionKey: 'territorio.caption1',
    className: 'row-span-1',
  },
  {
    src: "https://static.readdy.ai/image/13e0fae89bbbd02d36954a6d83bda5fb/101392395e8c053a73460f320faac114.png",
    captionKey: 'territorio.caption2',
    className: 'row-span-2',
  },
  {
    src: "https://static.readdy.ai/image/13e0fae89bbbd02d36954a6d83bda5fb/1018bb0225cb5b21b719b0287e9d23c5.png",
    captionKey: 'territorio.caption3',
    className: 'row-span-1',
  },
  {
    src: "https://static.readdy.ai/image/13e0fae89bbbd02d36954a6d83bda5fb/7a6c1ae8bfe7e02a9b821ee6228c29a1.png",
    captionKey: 'territorio.caption4',
    className: 'row-span-1',
  },
];

const TerritorioSection = () => {
  const { t } = useTranslation();
  const header = useScrollAnimation();
  const grid = useScrollAnimation({ threshold: 0.05 });
  const text = useScrollAnimation({ threshold: 0.2 });

  const titleLines = t('territorio.title').split('\n');

  return (
    <section id="territorio" className="bg-white py-24 md:py-32 px-6">
      <div className="max-w-7xl mx-auto">

        {/* Header */}
        <div
          ref={header.ref}
          className={`text-center mb-16 fade-in-up ${header.isVisible ? 'visible' : ''}`}
        >
          <p className="font-sans text-xs tracking-widest2 uppercase text-terracotta-300 mb-5">
            {t('territorio.overline')}
          </p>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-anthracite-800 font-light leading-tight max-w-3xl mx-auto">
            {titleLines.map((line, i) => (
              <span key={i}>
                {line}
                {i < titleLines.length - 1 && <br />}
              </span>
            ))}
          </h2>
        </div>

        {/* Photo Grid */}
        <div
          ref={grid.ref}
          className={`grid grid-cols-1 md:grid-cols-3 md:grid-rows-2 gap-4 md:gap-6 md:h-[620px] lg:h-[680px] mb-16 fade-in ${grid.isVisible ? 'visible' : ''}`}
        >
          {/* Large left — Mondavio, spans full height */}
          <div className="relative group overflow-hidden rounded-lg aspect-[3/4] md:aspect-auto md:col-span-1 md:row-span-2">
            <img
              src={PHOTOS[1].src}
              alt={`${t(PHOTOS[1].captionKey)} — immobili borghi Marche`}
              title={`${t(PHOTOS[1].captionKey)} — buy property Marche Italy`}
              className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            <p className="absolute bottom-5 left-5 font-sans text-sm font-medium text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300 tracking-wide">
              {t(PHOTOS[1].captionKey)}
            </p>
          </div>

          {/* Top right — wide, spans 2 columns */}
          <div className="relative group overflow-hidden rounded-lg aspect-[16/9] md:aspect-auto md:col-span-2 md:row-span-1">
            <img
              src={PHOTOS[0].src}
              alt={`${t(PHOTOS[0].captionKey)} — Castelfidardo real estate`}
              title={`${t(PHOTOS[0].captionKey)} — Castelfidardo real estate`}
              className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            <p className="absolute bottom-5 left-5 font-sans text-sm font-medium text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300 tracking-wide">
              {t(PHOTOS[0].captionKey)}
            </p>
          </div>

          {/* Bottom mid */}
          <div className="relative group overflow-hidden rounded-lg aspect-[4/3] md:aspect-auto md:col-span-1 md:row-span-1">
            <img
              src={PHOTOS[2].src}
              alt={`${t(PHOTOS[2].captionKey)} — borghi storici Marche`}
              title={`${t(PHOTOS[2].captionKey)} — borghi storici Marche`}
              className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            <p className="absolute bottom-5 left-5 font-sans text-sm font-medium text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300 tracking-wide">
              {t(PHOTOS[2].captionKey)}
            </p>
          </div>

          {/* Bottom right */}
          <div className="relative group overflow-hidden rounded-lg aspect-[4/3] md:aspect-auto md:col-span-1 md:row-span-1">
            <img
              src={PHOTOS[3].src}
              alt={`${t(PHOTOS[3].captionKey)} — immobili borghi Marche`}
              title={`${t(PHOTOS[3].captionKey)} — colline marchigiane`}
              className="absolute inset-0 w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            <p className="absolute bottom-5 left-5 font-sans text-sm font-medium text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300 tracking-wide">
              {t(PHOTOS[3].captionKey)}
            </p>
          </div>
        </div>

        {/* Descriptive text */}
        <div
          ref={text.ref}
          className={`max-w-2xl mx-auto text-center fade-in-up ${text.isVisible ? 'visible' : ''}`}
        >
          <p className="font-sans text-base md:text-lg text-anthracite-500 leading-loose">
            {t('territorio.text').split(t('territorio.keyword1')).map((part, i, arr) => (
              <span key={i}>
                {part}
                {i < arr.length - 1 && (
                  <em className="cormorant-italic text-terracotta-300 not-italic">
                    {t('territorio.keyword1')}
                  </em>
                )}
              </span>
            ))}
          </p>
        </div>
      </div>
    </section>
  );
};

export default TerritorioSection;
